import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as api from '../api/client';
import { AppError } from '../api/request';
import { useAuth } from '../context/AuthContext';
import OnboardingShell from './OnboardingShell';
import { clearOnboardingDraft, saveOnboardingStep } from './samples';
import { postAuthPath } from './destination';

const OAUTH_ERROR_KEYS: Record<string, string> = {
  oauth_denied: 'accounts.oauthError.denied',
  no_instagram_business: 'accounts.oauthError.noBusiness',
  account_limit_reached: 'accounts.oauthError.limitReached',
};

export default function ConnectPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [accounts, setAccounts] = useState<api.InstagramAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState('');

  async function loadAccounts() {
    try {
      const list = await api.listInstagramAccounts();
      setAccounts(list);
    } catch {
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAccounts();
  }, []);

  useEffect(() => {
    saveOnboardingStep('/onboarding/connect', user?.id);
  }, [user?.id]);

  useEffect(() => {
    const connected = params.get('instagram_connected');
    const errCode = params.get('instagram_error');
    if (connected) {
      void loadAccounts();
      setParams({}, { replace: true });
    } else if (errCode) {
      setError(t(OAUTH_ERROR_KEYS[errCode] ?? 'accounts.oauthError.generic'));
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connected = accounts[0];

  async function connect() {
    setError('');
    setConnecting(true);
    try {
      const url = await api.getInstagramOAuthURL();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof AppError ? err.message : t('accounts.connectError'));
      setConnecting(false);
    }
  }

  async function finish(skip = false) {
    if (!skip && !connected) {
      setError(t('onboarding.connect.required'));
      return;
    }
    setError('');
    setFinishing(true);
    try {
      const updated = await api.completeOnboarding();
      clearOnboardingDraft();
      await refreshUser();
      navigate(postAuthPath(updated), { replace: true });
    } catch (err) {
      setError(err instanceof AppError ? err.message : t('onboarding.connect.finishError'));
      setFinishing(false);
    }
  }

  return (
    <OnboardingShell title={t('onboarding.connect.title')}>
      <div className="mb-10">
        <div className="mb-3 text-xs font-semibold text-[#ff9a6c]">{t('onboarding.stepOf', { current: 3, total: 4 })}</div>
        <h2 className="m-0 text-[36px] font-extrabold tracking-[-0.02em]">{t('onboarding.connect.title')}</h2>
      </div>

      <div className="mb-8 rounded-xl border border-[#2b1f33] bg-[#221733] p-8">
        <div className="mb-6 flex justify-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#fd5949] via-[#d6249f] to-[#285AEB] text-[32px] font-bold text-white">
            📷
          </div>
        </div>
        <p className="mb-6 text-center text-[15px] text-[#c9b8d1]">{t('onboarding.connect.body')}</p>

        {loading ? (
          <p className="text-center text-sm text-[#a8899f]">{t('common.loading')}</p>
        ) : connected ? (
          <div className="rounded-lg border border-[#3fb950] bg-[#1a3a1a] px-3 py-3 text-center text-[13px] font-semibold text-[#3fb950]">
            ✓ @{connected.igUsername || t('accounts.noUsername')} {t('onboarding.connect.connectedSuffix')}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => void connect()}
            disabled={connecting}
            className="w-full rounded-lg border-none bg-[#e1306c] py-3.5 text-base font-bold text-white disabled:opacity-60"
          >
            {connecting ? t('common.redirecting') : t('onboarding.connect.cta')}
          </button>
        )}
      </div>

      <div className="mb-8 rounded-lg border border-[#3d2e46] bg-[#2a1f3d] p-4 text-[13px] text-[#b892ff]">
        <div className="mb-2 font-semibold">{t('onboarding.connect.securityTitle')}</div>
        <ul className="m-0 list-disc space-y-1 pl-5 text-[#c9b8d1]">
          <li>{t('onboarding.connect.security1')}</li>
          <li>{t('onboarding.connect.security2')}</li>
          <li>{t('onboarding.connect.security3')}</li>
        </ul>
      </div>

      {error && <p className="mb-4 text-sm text-[#ff8a9e]">{error}</p>}

      <div className="flex gap-3">
        <Link
          to="/onboarding/preview"
          className="flex-1 rounded-lg border border-[#3d2e46] bg-transparent py-3 text-center text-sm font-semibold text-[#f5eef7]"
        >
          {t('onboarding.back')}
        </Link>
        <button
          type="button"
          onClick={() => void finish(false)}
          disabled={finishing || !connected}
          className="flex-1 rounded-lg border-none bg-[#ff6f91] py-3 text-sm font-semibold text-[#2b0e18] hover:bg-[#ff85a3] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {finishing ? t('common.loading') : t('onboarding.continue')}
        </button>
      </div>
      {!connected && (
        <button
          type="button"
          onClick={() => void finish(true)}
          disabled={finishing}
          className="mt-3 w-full text-center text-sm font-semibold text-[#a8899f] hover:text-[#f5eef7] disabled:opacity-50"
        >
          {t('onboarding.connect.skip')}
        </button>
      )}
    </OnboardingShell>
  );
}
